import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { controlAccessPopUp } from '@app/modules/ui-notification/ControlAccessDialog/controlaccess.component';
import { Licence } from '@core/auth/guards/license.guard';
import { SpaceService } from '@core/spaces/spaces.service';
import { UserService } from '@core/user/user.service';
import { IconsModule } from '@core/utils/icons.module';
import { VersiongService } from '@core/versionning/versionning.service';
import { Version } from '@core/versionning/versionning.types';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        // FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        // LanguagesComponent,
        // FuseFullscreenComponent,
        // SearchComponent,
        // ShortcutsComponent,
        // MessagesComponent,
        NotificationsComponent,
        UserComponent,
        RouterOutlet,
        // QuickChatComponent,
        IconsModule,
        CommonModule
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    navigationAppearance: 'default' | 'dense' = 'dense';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    ariane: any;
    isAdmin: boolean = false;
    isFreemium: boolean = false;
    hasCDCSpaceAccess: boolean = false;
    headerClasses: string;
    sideBarClasses: string;
    currentSpace: string;
    version: Version = null;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _userService: UserService,
        private cdRef: ChangeDetectorRef,
        private _versionService: VersiongService,
        private _spaceService: SpaceService,
        private _dialog: MatDialog
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        // this._navigationService.navigation$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((navigation: Navigation) => {
        //         this.navigation = navigation;
        //     });

        this._spaceService.currentSpaceMenu$.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            })

        this._spaceService.currentSpaceHeaderClass$.pipe(takeUntil(this._unsubscribeAll))
            .subscribe(c => {
                this.headerClasses = c;
            })

        this._spaceService.currentSpaceSidebarClass$.pipe(takeUntil(this._unsubscribeAll))
            .subscribe(c => {
                this.sideBarClasses = c;
                //console.log("Here", c)
            })

        this._spaceService.currentSpace$.pipe(takeUntil(this._unsubscribeAll))
            .subscribe(s => {
                this.currentSpace = s;
            })

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall
                    ? 'default'
                    : 'dense';
            });


        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe(
            u => {
                this.isAdmin = u.accessLevel == Licence.CorporatingsAdmin
                this.isFreemium = u.accessLevel == Licence.Freemium
                this.hasCDCSpaceAccess = u.spaces.includes("CDC")
            }
        )

        this._versionService.version$.pipe(takeUntil(this._unsubscribeAll)).subscribe(
            v => {
                this.version = v;
                //this.titleService.setTitle((v.appEnv.toUpperCase() == "PROD" ? "" : v.appEnv.toUpperCase() + " - ") + "LENS - Corporatings")
            }
        )
    }

    /**
     * 
     */
    ngAfterViewInit(): void {
        this._navigationService.ariane$.pipe(takeUntil(this._unsubscribeAll)).subscribe(ari => { this.ariane = ari; this.cdRef.detectChanges(); })
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
    }




    /**
     * Go to Admin
     */
    goToAdmin(event: MouseEvent) {
        if (event.ctrlKey) {
            window.open("/admin", '_blank')
        } else
            this._router.navigateByUrl("/admin")
    }



    /**
     * Subscribe to premium
     */
    onSubscribeClick() {
        controlAccessPopUp(
            this._dialog,
            'Subscribe to a <span class="text-red-500">Premium</span> and unlock unlimited access to all features',
            'Subscribe to Premium'
        )
    }


    /**
     * Open hubspot
     */
    goToHubspot() {
        window.open('https://meetings.hubspot.com/alexandre-prat-fourcade/clone', '_blank')
    }

    goContactByMail() {
        window.open('mailto:contact@corporatings.com')

    }



    toggleCDCSpace() {
        if (this.currentSpace == 'normal') {
            this._spaceService.setSpace('CDC').subscribe()
            this._router.navigateByUrl("/cdc-space-home")
        }
        else {
            this._spaceService.setSpace('normal').subscribe()
            this._router.navigateByUrl("/home")
        }

    }
}
